import { Link } from "react-router-dom";
import { ChevronRight, Globe } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { AddContainerDialog } from "@/components/containers/AddContainerDialog";
import { useContainers } from "@/hooks/useContainers";
import { formatDate } from "@/lib/utils";
import { cn } from "@/lib/utils";
import type { ContainerSummaryDto } from "@/types/api";

export function DomainsPage() {
  const { data, isLoading, error } = useContainers();

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* ── Page header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Domains</h1>
          <p className="mt-1 text-sm text-muted-foreground sm:text-base">
            Every domain on your account and what's running on it.
          </p>
        </div>
        <AddContainerDialog />
      </div>

      {/* ── Body ── */}
      {isLoading ? (
        <ListSkeleton />
      ) : error ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Couldn't load your domains. Check the API is running and try again.
          </CardContent>
        </Card>
      ) : !data || data.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Desktop ≥ md: table */}
          <Card className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Domain</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Mailboxes</TableHead>
                  <TableHead>Services</TableHead>
                  <TableHead>Renews</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map(c => (
                  <DomainRow key={c.id} container={c} />
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Mobile < md: card list (tables wrap badly on narrow screens) */}
          <div className="grid gap-3 md:hidden">
            {data.map(c => (
              <DomainCard key={c.id} container={c} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Desktop row ──────────────────────────────────────────────
function DomainRow({ container }: { container: ContainerSummaryDto }) {
  return (
    <TableRow>
      <TableCell>
        <Link
          to={`/domains/${container.id}`}
          className="group flex items-center gap-2 font-medium text-foreground hover:text-primary"
        >
          <Globe className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
          <span>{container.domain_name}</span>
        </Link>
        {container.registrar && (
          <p className="ml-6 text-xs text-muted-foreground">{container.registrar}</p>
        )}
      </TableCell>
      <TableCell>
        <StatusBadge status={container.status} />
      </TableCell>
      <TableCell>
        <span className="font-mono text-sm">
          {container.mailboxes_used}
          <span className="text-muted-foreground"> / {container.mailboxes_allocated || "—"}</span>
        </span>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {container.services_count > 0 ? `${container.services_count} active` : "None yet"}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {formatDate(container.renewal_date ?? undefined)}
      </TableCell>
      <TableCell>
        <Link
          to={`/domains/${container.id}`}
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={`Open ${container.domain_name}`}
        >
          <ChevronRight className="h-4 w-4" />
        </Link>
      </TableCell>
    </TableRow>
  );
}

// ── Mobile card ──────────────────────────────────────────────
function DomainCard({ container }: { container: ContainerSummaryDto }) {
  return (
    <Link to={`/domains/${container.id}`} className="block">
      <Card className="transition-shadow hover:shadow-brand-md">
        <CardContent className="flex items-center gap-3 p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-accentSoft text-primary">
            <Globe className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="truncate font-medium text-foreground">{container.domain_name}</p>
              <StatusBadge status={container.status} />
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {container.mailboxes_used}/{container.mailboxes_allocated || "—"} mailboxes
              {container.services_count > 0 ? ` · ${container.services_count} services` : ""}
            </p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </CardContent>
      </Card>
    </Link>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant: React.ComponentProps<typeof Badge>["variant"] =
    status === "active" ? "success" : status === "draft" ? "outline" : "secondary";
  return (
    <Badge variant={variant} className={cn("uppercase", status === "draft" && "text-muted-foreground")}>
      {status}
    </Badge>
  );
}

// ── States ────────────────────────────────────────────────────
function ListSkeleton() {
  return (
    <Card>
      <CardContent className="space-y-3 py-5">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-accentSoft text-primary">
          <Globe className="h-6 w-6" />
        </div>
        <div>
          <p className="font-display text-lg font-semibold">No domains yet</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Add your first domain to start provisioning mailboxes, websites and SSL.
          </p>
        </div>
        <div className="mt-2">
          <AddContainerDialog />
        </div>
      </CardContent>
    </Card>
  );
}
